import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Trash2, Edit, CreditCard, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getStatusLabel, getStatusColor } from '@/utils/formatters';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMyProperties();
  }, [isAuthenticated]);

  const fetchMyProperties = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/properties/user/my-properties', {
        params: { limit: 50 }
      });
      setProperties(data.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar tus propiedades');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta propiedad?')) return;

    try {
      await axiosInstance.delete(`/properties/${propertyId}`);
      toast.success('Propiedad eliminada');
      fetchMyProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Error al eliminar la propiedad');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* User Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200 mb-8"
          data-testid="user-info"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2">{user?.name}</h1>
                <div className="space-y-1">
                  <div className="flex items-center text-slate-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <span>{user?.email}</span>
                  </div>
                  {user?.phone && (
                    <div className="flex items-center text-slate-600">
                      <Phone className="w-4 h-4 mr-2" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {user?.role === 'ADMIN' && (
              <Badge className="bg-accent text-white">Administrador</Badge>
            )}
          </div>
        </motion.div>

        {/* My Properties */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold text-slate-900">Mis Propiedades</h2>
            <Button onClick={() => navigate('/publicar')} className="bg-primary hover:bg-primary-hover" data-testid="publish-button">
              Publicar Nueva Propiedad
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-slate-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-list">
              {properties.map((property) => (
                <div key={property.id} className="relative">
                  <PropertyCard property={property} />
                  <div className="absolute top-4 left-4 z-10">
                    <Badge className={getStatusColor(property.status)}>
                      {getStatusLabel(property.status)}
                    </Badge>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    {property.status === 'DRAFT' || property.status === 'PENDING' ? (
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 bg-accent hover:bg-accent-hover"
                        onClick={() => navigate(`/pagos?property=${property.id}&plan=publish`)}
                        data-testid={`pay-publish-${property.id}`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pagar para Publicar
                      </Button>
                    ) : property.status === 'APPROVED' && !property.is_featured ? (
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 bg-secondary hover:bg-secondary-hover"
                        onClick={() => navigate(`/pagos?property=${property.id}&plan=feature_7`)}
                        data-testid={`pay-feature-${property.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Destacar
                      </Button>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      className={property.status === 'DRAFT' || property.status === 'PENDING' ? 'flex-1' : 'flex-1'}
                      onClick={() => navigate(`/editar/${property.id}`)}
                      data-testid={`edit-${property.id}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(property.id)}
                      data-testid={`delete-${property.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="no-properties">
              <p className="text-xl text-slate-600 mb-4">No tienes propiedades publicadas</p>
              <Button onClick={() => navigate('/publicar')} className="bg-primary hover:bg-primary-hover">
                Publicar tu Primera Propiedad
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
